import React, { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import type { BDAction } from '../../pages/BDApprovals';

interface Props {
  action: BDAction;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * BD-03. One free-text field per action_type — matches the single field
 * bdActionReplyParser.js lets a Slack thread reply patch, so the Jarvis and
 * Slack edit paths stay consistent rather than one supporting more than the
 * other. Saving creates a NEW pending item (the original is superseded) —
 * it does not approve anything.
 */
const EDITABLE_FIELD: Record<string, string> = {
  email_draft: 'body',
  email_send: 'body',
  zoho_note: 'content',
  zoho_field_update: 'value',
  pm_task: 'title',
  pm_comment: 'content',
};

const ActionEditModal: React.FC<Props> = ({ action, onClose, onSaved }) => {
  const field = EDITABLE_FIELD[action.action_type];
  const [value, setValue] = useState<string>(field ? (action.payload?.[field] ?? '') : '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!field) return;
    setSaving(true);
    try {
      const editedPayload = { ...action.payload, [field]: value };
      await api.post(`/api/bd-approvals/${action.id}/edit`, { editedPayload });
      toast.success('Revised version created — it still needs its own approval');
      onSaved();
    } catch (err) {
      toast.error('Failed to save edit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Edit proposal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {!field ? (
            <p className="text-sm text-gray-500">
              This proposal type has no single field to edit here — reject it and ask for a revised one instead.
            </p>
          ) : (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{field}</label>
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={6}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border text-gray-700 hover:bg-gray-100">
            Cancel
          </button>
          {field && (
            <button
              onClick={save}
              disabled={saving || !value.trim()}
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save as new proposal'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionEditModal;
